import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';

import { AccessRoles } from 'constants/enums';

import { login, consumeAuthorizationCode, logoutWithDelay } from './service';

export const initialState: IOAuthState = {
  data: null,
  loading: false,
  error: false,
  loginError: null,
  authType: 'oAuth',
  expiresAt: null,
  logoutRedirection: null
};

const slice = createSlice({
  name: 'oauth',
  initialState,
  reducers: {
    logout: () => initialState,
    logoutWithError: (_state, action: PayloadAction<'noAccess'>) => ({
      ...initialState,
      loginError: action.payload
    }),
    setAuthType: (state, action: PayloadAction<IOAuthTypes>) => {
      state.authType = action.payload;
    },
    refreshTokens: (
      state,
      action: PayloadAction<{
        access_token: string;
        refresh_token: string;
      }>
    ) => {
      if (state.data) {
        state.data.access_token = action.payload.access_token;
        state.data.refresh_token = action.payload.refresh_token;
      }
    },
    setAvatar: (state, action: PayloadAction<{ avatar?: string; aid: string }>) => {
      if (state.data) {
        state.data.avatar = action.payload.avatar;
      }
    },
    /**
     * The second factor has been presented, so the session is no longer holding one open.
     *
     * `data.code` is whatever the login answered - 67 "enter the code", 68 "no number on file" -
     * and nothing used to clear it, because nothing used to read it after the TFA screen. Now
     * DashboardWrapper withholds the access token while it is set, to stop the dashboard firing a
     * wave of requests the 7000 will 401 while the factor is outstanding. That gate has to be
     * released here or it never opens: the code stays at 67, the groups fetch never runs, and the
     * dashboard spins for the rest of the session (dev01, 2026-09-15).
     */
    tfaValidated: (state) => {
      if (state.data) {
        state.data.code = undefined;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(isAnyOf(login.pending, consumeAuthorizationCode.pending), (state, action) => {
        state.loading = true;
        state.error = false;
        state.loginError = null;
      })
      .addMatcher(isAnyOf(login.fulfilled, consumeAuthorizationCode.fulfilled), (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.expiresAt = DateTime.now()
          .plus({ minutes: import.meta.env.VITE_SESSION_EXPIRE || 720 })
          .toSeconds();
      })
      .addMatcher(isAnyOf(login.rejected, consumeAuthorizationCode.rejected), (state, action) => {
        state.loading = false;
        state.error = true;
      })
      .addMatcher(isAnyOf(logoutWithDelay.pending), (state) => {
        state.loading = true;
        state.error = false;
      })
      .addMatcher(isAnyOf(logoutWithDelay.fulfilled), (state) => {
        state.data = initialState.data;
        state.loading = false;
        state.expiresAt = null;
        state.logoutRedirection = Date.now();
      });
  }
});

const getUserAid = (state: IAppState) => state.oauth.data?.aid;
const selectClinicianDisplayName = (state: IAppState) => state.oauth.data?.displayName;

const isPatientAuth = (state: IAppState) => !state.oauth.data?.role;

const getUserRoles = (state: IAppState) => state.oauth.data?.roles?.map((role) => parseInt(role));

export { getUserAid, selectClinicianDisplayName, isPatientAuth, getUserRoles };

export const { logout, logoutWithError, refreshTokens, setAvatar, setAuthType, tfaValidated } =
  slice.actions;
export const oauthReducer = slice.reducer;
